import { DifferenceModule, SortDifferencesOption } from './types';
// ---------------------------------------------------------------------------
// Per-module partitioning
// ---------------------------------------------------------------------------
export const partitionByModule = (differences) => {
    return differences.reduce((modules, difference, inputIndex) => {
        const bucket = modules.get(difference.module) ?? [];
        bucket.push({ difference, inputIndex });
        modules.set(difference.module, bucket);
        return modules;
    }, new Map());
};
// ---------------------------------------------------------------------------
// Grouping helpers
// ---------------------------------------------------------------------------
/**
 * Builds DifferenceUnits from a flat list of indexed differences.
 *
 * `groupId` is the sole grouping key. Empty strings are already normalized to
 * undefined by the adapters, so any truthy `groupId` is a real grouping key.
 * `word` is not a grouping key — it is only used as the sort key for the
 * alphabetical sort option.
 *
 * When groupDifferences is false (VerifyAPI direct-import path), every
 * difference becomes its own singleton unit — no grouping at all, preserving
 * the straight 1, 2, 3… numbering that path requires for backward compatibility.
 *
 * When groupDifferences is true (default), differences sharing a `groupId`
 * are collected into one unit; differences with no `groupId` become singleton
 * units. The reading-order representative of each group is computed once here.
 */
export const buildDifferenceUnits = (differences, groupDifferences) => {
    if (!groupDifferences) {
        return differences.map((d) => ({ members: [d], representative: d }));
    }
    const keyedGroups = new Map();
    const ungrouped = [];
    for (const indexedDiff of differences) {
        const groupId = indexedDiff.difference.groupId;
        if (!groupId) {
            ungrouped.push({ members: [indexedDiff], representative: indexedDiff });
            continue;
        }
        const members = keyedGroups.get(groupId) ?? [];
        members.push(indexedDiff);
        keyedGroups.set(groupId, members);
    }
    const grouped = [...keyedGroups.values()].map((members) => ({
        members,
        representative: getReadingOrderRepresentative(members),
    }));
    return [...grouped, ...ungrouped];
};
// ---------------------------------------------------------------------------
// Reading-order helpers
// ---------------------------------------------------------------------------
export const getReadingOrderRepresentative = (members) => {
    if (members.length === 0) {
        throw new Error('getReadingOrderRepresentative requires at least one member');
    }
    return members.reduce((best, current) => compareIndexedDifferencesByReadingOrder(current, best) < 0 ? current : best);
};
export const compareIndexedDifferencesByReadingOrder = (a, b) => {
    const locationCompare = compareLocations(a.difference, b.difference);
    if (locationCompare !== 0)
        return locationCompare;
    return a.inputIndex - b.inputIndex;
};
const compareLocations = (a, b) => {
    const aLoc = a.location;
    const bLoc = b.location;
    if (!aLoc && !bLoc)
        return 0;
    if (!aLoc)
        return 1;
    if (!bLoc)
        return -1;
    if (aLoc.pageNumber !== bLoc.pageNumber)
        return aLoc.pageNumber - bLoc.pageNumber;
    if (aLoc.y !== bLoc.y)
        return aLoc.y - bLoc.y;
    return aLoc.x - bLoc.x;
};
// ---------------------------------------------------------------------------
// Sort comparator for group representatives
// ---------------------------------------------------------------------------
/**
 * Compares two DifferenceUnits for a given module using the options' sort chain:
 *   1. Resync pinning — resync representatives always float to the top.
 *   2. Active per-module sort (falls back to inspection-wide sortBy, then reading order).
 *   3. Reading-order tiebreak.
 */
export const compareUnits = (a, b, module, options) => {
    const resyncCompare = compareResyncPriority(a, b);
    if (resyncCompare !== 0)
        return resyncCompare;
    const requestedSortOption = options.sortOptions?.sortByPerModule?.[module] ??
        options.sortOptions?.sortBy ??
        SortDifferencesOption.readingOrder;
    // `alphabetical` is Spelling-only in the Verify UI (getDifferenceSortOption downgrades it to
    // reading order on any other module). Mirror that here so a leaked inspection-wide alphabetical
    // sortBy doesn't reorder non-spelling modules by word/source text and diverge from the UI's
    // numbering. fontSize is deliberately NOT guarded: the UI applies it wherever it leaks too, so
    // guarding it here would introduce the very divergence we're preventing.
    const sortOption = requestedSortOption === SortDifferencesOption.alphabetical && module !== DifferenceModule.spelling
        ? SortDifferencesOption.readingOrder
        : requestedSortOption;
    const sortCompare = compareBySortOption(a.representative.difference, b.representative.difference, sortOption, options);
    if (sortCompare !== 0)
        return sortCompare;
    return compareIndexedDifferencesByReadingOrder(a.representative, b.representative);
};
// ---------------------------------------------------------------------------
// Resync pinning
// ---------------------------------------------------------------------------
/**
 * Resync differences are always pinned to the top of their module, ahead of any active sort.
 * Within the resync block, higher resyncIds (more recent re-inspections) sort first.
 */
const compareResyncPriority = (a, b) => {
    const aId = getHighestResyncId(a);
    const bId = getHighestResyncId(b);
    if (aId !== undefined && bId === undefined)
        return -1;
    if (aId === undefined && bId !== undefined)
        return 1;
    if (aId !== undefined && bId !== undefined && aId !== bId)
        return bId - aId;
    return 0;
};
const getHighestResyncId = (unit) => {
    return unit.members.reduce((highest, member) => {
        const id = member.difference.resyncId;
        if (id === undefined)
            return highest;
        return highest === undefined ? id : Math.max(highest, id);
    }, undefined);
};
// ---------------------------------------------------------------------------
// Sort-mode comparators
// ---------------------------------------------------------------------------
const compareBySortOption = (a, b, sortOption, options) => {
    switch (sortOption) {
        case SortDifferencesOption.differenceStatus:
            return compareOptionalStrings(getStatusTitle(a, options), getStatusTitle(b, options), true);
        case SortDifferencesOption.differenceType:
            return compareOptionalStrings(a.displayName, b.displayName);
        case SortDifferencesOption.fontSize:
            return (a.fontPointSize ?? 0) - (b.fontPointSize ?? 0);
        case SortDifferencesOption.alphabetical:
            return compareOptionalStrings(a.word ?? a.sourceText ?? '', b.word ?? b.sourceText ?? '');
        case SortDifferencesOption.readingOrder:
            return 0;
        default: {
            // Falls back to reading order (no-op comparator) if sortOption is an invalid
            // value that reached here at runtime — e.g. a bad/stale value from the API
            // or persisted data that bypassed compile-time type checking.
            //
            // The `never` assignment below is a compile-time-only safety net: it forces a
            // build error if a new SortDifferencesOption is ever added without a case
            // above, so this default is never accidentally handling a real, known option.
            const _exhaustive = sortOption;
            return 0;
        }
    }
};
const getStatusTitle = (difference, options) => {
    return difference.statusId ? options.statusTitlesById?.[difference.statusId] : undefined;
};
/**
 * Locale-aware case-insensitive string comparison.
 * When undefinedLast is true, undefined values sort after defined ones.
 * When undefinedLast is false (default), undefined values sort before defined ones.
 */
export const compareOptionalStrings = (a, b, undefinedLast = false) => {
    if (undefinedLast) {
        if (a === undefined && b !== undefined)
            return 1;
        if (a !== undefined && b === undefined)
            return -1;
    }
    return (a ?? '').toLocaleLowerCase().localeCompare((b ?? '').toLocaleLowerCase());
};
//# sourceMappingURL=sorting.js.map